import { MINIMUM_LAP_TIME } from './config';

const calculateAverage = (laps) => {
  if (!laps || laps.length === 0) return 0;
  return laps.reduce((a, b) => {
    const lapTime = typeof b === 'object' ? b.time : b;
    return a + lapTime;
  }, 0) / laps.length;
};

export const useCompetitionHandlers = ({
  isRunning,
  setIsRunning,
  isPaused,
  setIsPaused,
  currentTime,
  setCurrentTime,
  startTime,
  setStartTime,
  totalStartTime,
  setTotalStartTime,
  pausedTime,
  setPausedTime,
  totalTime,
  setTotalTime,
  currentSwimmerId,
  setCurrentSwimmerId,
  swimmersList,
  swimmersData,
  setSwimmersData,
  hasFirstLap,
  setHasFirstLap,
  stopConfirmation,
  setStopConfirmation,
  isCompetitionEnded,
  setIsCompetitionEnded,
  startCompetition,
  endCompetition,
  addResult,
  poolLength,
  raceDistance,
  showSuccess,
  showError,
  showInfo
}) => {
  const handleStart = () => {
    try {
      if (!isRunning) {
        const now = Date.now();
        setStartTime(now);
        setCurrentTime(0);
        localStorage.setItem('startTime', now.toString());
        
        setIsRunning(true);
        setIsPaused(false);
        
        if (totalTime === 0) {
          setTotalStartTime(now);
          localStorage.setItem('totalStartTime', now.toString());
          if (Array.isArray(swimmersList) && swimmersList.length > 0) {
            startCompetition(swimmersList);
            showSuccess('Competição iniciada com sucesso!');
          }
        } else {
          showInfo('Competição retomada');
        }
      }
    } catch (error) {
      showError('Erro ao iniciar competição');
      console.error('Erro ao iniciar:', error);
    }
  };

  const handleStop = () => {
    try {
      if (currentTime >= MINIMUM_LAP_TIME) {
        const lapTime = Date.now() - startTime;
        const newLaps = [...(swimmersData[currentSwimmerId]?.laps || []), lapTime];
        const distance = raceDistance || poolLength;
        
        const updatedData = {
          ...swimmersData,
          [currentSwimmerId]: {
            currentLapTime: lapTime,
            laps: newLaps,
            totalDistance: newLaps.length * distance,
            average: calculateAverage(newLaps),
            poolLength: poolLength,
            raceDistance: raceDistance
          }
        };
        
        setSwimmersData(updatedData);
        localStorage.setItem('swimmersData', JSON.stringify(updatedData));
        
        addResult(currentSwimmerId, lapTime);
        setHasFirstLap(true);
        localStorage.setItem('hasFirstLap', 'true');
        showSuccess(`Volta registrada para ${currentSwimmerId}`);
      }

      setCurrentTime(0);
      setPausedTime(0);
      setStartTime(null);
      setIsRunning(false);
      setIsPaused(true);

      localStorage.setItem('currentTime', '0');
      localStorage.setItem('pausedTime', '0');
      localStorage.setItem('startTime', '');
      localStorage.setItem('isRunning', 'false');
      localStorage.setItem('isPaused', 'true');
      
      showInfo('Competição pausada');
    } catch (error) {
      showError('Erro ao parar competição');
      console.error('Erro ao parar:', error);
    }
  };

  const handleNextSwimmer = () => {
    try {
      if (currentTime >= MINIMUM_LAP_TIME) {
        const lapTime = Date.now() - startTime;
        const newLaps = [...(swimmersData[currentSwimmerId]?.laps || []), lapTime];
        const distance = raceDistance || poolLength;
        
        const updatedData = {
          ...swimmersData,
          [currentSwimmerId]: {
            currentLapTime: lapTime,
            laps: newLaps,
            totalDistance: newLaps.length * distance,
            average: calculateAverage(newLaps),
            poolLength: poolLength,
            raceDistance: raceDistance
          }
        };
        
        setSwimmersData(updatedData);
        localStorage.setItem('swimmersData', JSON.stringify(updatedData));
        
        addResult(currentSwimmerId, lapTime);
        setHasFirstLap(true);
        localStorage.setItem('hasFirstLap', 'true');
        
        setCurrentTime(0);
        setPausedTime(0);
        
        const currentIndex = swimmersList.indexOf(currentSwimmerId);
        const nextSwimmer = currentIndex + 1 >= swimmersList.length ? swimmersList[0] : swimmersList[currentIndex + 1];
        
        setCurrentSwimmerId(nextSwimmer);
        localStorage.setItem('currentSwimmerId', nextSwimmer);

        const newStartTime = Date.now();
        setStartTime(newStartTime);
        localStorage.setItem('startTime', newStartTime.toString());
        
        setIsRunning(true);
        setIsPaused(false);
        
        localStorage.setItem('currentTime', '0');
        localStorage.setItem('pausedTime', '0');
        localStorage.setItem('isRunning', 'true');
        localStorage.setItem('isPaused', 'false');

        showSuccess(`Volta registrada para ${currentSwimmerId}. Próximo nadador: ${nextSwimmer}`);
      } else {
        showError('Tempo mínimo entre voltas não atingido');
      }
    } catch (error) {
      showError('Erro ao registrar volta');
      console.error('Erro ao registrar volta:', error);
    }
  };

  const handleStopClick = () => {
    setStopConfirmation(true);
  };

  const handleStopConfirm = () => {
    try {
      const finalTotalTime = totalStartTime ? Date.now() - totalStartTime : totalTime;
      
      setIsRunning(false);
      setIsPaused(true);
      setStartTime(null);
      setTotalStartTime(null);
      setTotalTime(finalTotalTime);
      setIsCompetitionEnded(true);
      
      if (currentTime >= MINIMUM_LAP_TIME) {
        const lapTime = Date.now() - startTime;
        const newLaps = [...(swimmersData[currentSwimmerId]?.laps || []), lapTime];
        const distance = raceDistance || poolLength;
        
        const updatedData = {
          ...swimmersData,
          [currentSwimmerId]: {
            currentLapTime: lapTime,
            laps: newLaps,
            totalDistance: newLaps.length * distance,
            average: calculateAverage(newLaps),
            poolLength: poolLength,
            raceDistance: raceDistance
          }
        };
        
        setSwimmersData(updatedData);
        localStorage.setItem('swimmersData', JSON.stringify(updatedData));
        addResult(currentSwimmerId, lapTime);
      }
      
      endCompetition(finalTotalTime);
      setStopConfirmation(false);
      setCurrentTime(0);
      
      localStorage.setItem('competitionStarted', 'false');
      localStorage.setItem('isCompetitionEnded', 'true');
      localStorage.setItem('totalTime', finalTotalTime.toString());
      localStorage.setItem('isRunning', 'false');
      localStorage.setItem('isPaused', 'true');
      localStorage.setItem('currentTime', '0');
      localStorage.setItem('startTime', '');
      localStorage.setItem('totalStartTime', '');

      showSuccess('Competição finalizada com sucesso!');
    } catch (error) {
      showError('Erro ao finalizar competição');
      console.error('Erro ao finalizar:', error);
    }
  };

  const handleStopCancel = () => {
    setStopConfirmation(false);
  };

  return {
    handleStart,
    handleStop,
    handleNextSwimmer,
    handleStopClick,
    handleStopConfirm,
    handleStopCancel
  };
};